import FileOverridePrompt from 'features/Integrations/features/AudioManager/components/FileOverridePrompt/FileOverridePrompt';
import { useEffect, useRef, useState } from 'react';
import { GetExtention, RemoveExtention } from 'utils/strings/string.utils';
import SelectPrompt from './components/SelectPrompt/SelectPrompt';
import SelectedFileControl from './components/SelectedFileControl/SelectedFileControl';
import { useSendRecording } from 'features/Integrations/features/AudioManager/hooks/useSendRecording';
import { toast } from 'react-toastify';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import {
  getAcceptedAudioFiles,
  getAudioFileUploadError,
  getAudioNotificationSubject,
} from '../../utils/audioFiles.utils';

const MODES = {
  none: 0,
  'file-loaded': 1,
};

const FileSelect = ({
  file,
  fileTypes = ['*'],
  maxFileSize = 5000000,
  onChange = () => {},
  onSave = () => {},
}) => {
  const { uploadProgress, clearProgress, sendRecording } = useSendRecording();

  const timeoutRef = useRef(null);

  const [mode, setMode] = useState(MODES['none']);
  const [localFile, setLocalFile] = useState(null);
  const [duplicateFile, setDuplicateFile] = useState(false);
  const [promptOverride, setPromptOverride] = useState(false);

  useEffect(() => {
    if (file) setMode(MODES['file-loaded']);
    else setMode(MODES['none']);
  }, [file]);

  useEffect(() => {
    if (uploadProgress !== null) setMode(MODES['file-loaded']);
    else setMode(MODES['none']);
  }, [uploadProgress]);

  // Shared file upload logic
  const handleFileUpload = async (file) => {
    if (!file) return;
    const validationError = getAudioFileUploadError(file, maxFileSize);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLocalFile(file);

    try {
      const ret = await sendRecording(file, RemoveExtention(file.name), GetExtention(file.name));

      if (ret?.data?.expireTime)
        timeoutRef.current = setTimeout(() => {
          onChange(null);
        }, ret.data.expireTime);

      if (ret?.status === 200) {
        onChange(file);
        setDuplicateFile(ret?.data?.duplicateFile || false);
        notifySuccessToast(getAudioNotificationSubject(file.name), { isAddOperation: true });
      }
    } catch (err) {
      setLocalFile(null);
      notifyErrorToast(getAudioNotificationSubject(file.name), { isAddOperation: true });
    }
  };

  // updateFile runs when you select a file through "choose file" button.
  const updateFile = async (e) => {
    const newFile = e.target.files[0];
    await handleFileUpload(newFile);
  };

  // Handles file upload via drag-and-drop
  const onFileDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length < 1) return;

    const acceptedFiles = getAcceptedAudioFiles(e.dataTransfer.files, fileTypes);
    if (acceptedFiles.length > 0) {
      await handleFileUpload(acceptedFiles[0]);
    }
  };

  // Clears the selected file and upload progress
  const clearFile = () => {
    setLocalFile(null);
    clearProgress();
    onChange(null);
  };

  // Handles the save button click, checks for duplicates and prompts if needed
  const onSaveInt = () => {
    if (!duplicateFile) {
      onSaveResolve(false);
      return;
    }

    setPromptOverride(true);
  };

  // Called when overriding an existing file, proceeds with saving the new file and overriding the old one
  const onSaveResolve = (override) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setPromptOverride(false);
    onSave(false, override);
    notifySuccessToast('File saved successfully', { isAddOperation: true });
    clearFile();
  };

  return (
    <div className="flex-fill h-100 d-flex flex-column justify-content-center align-items-center">
      {mode === MODES['none'] && (
        <SelectPrompt fileTypes={fileTypes} onUpdateFile={updateFile} onFileDrop={onFileDrop} />
      )}

      {mode === MODES['file-loaded'] && (
        <SelectedFileControl
          file={localFile || file}
          progress={uploadProgress}
          onClear={clearFile}
          onSave={onSaveInt}
        />
      )}

      <FileOverridePrompt
        fileName={file?.name}
        open={promptOverride}
        onClose={() => setPromptOverride(false)}
        onResolve={onSaveResolve}
      />
    </div>
  );
};

export default FileSelect;
